import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';
import SkeletonImage from './common/SkeletonImage';

const CareersSection = ({ careersData }) => {
  const intl = useIntl();
  const { language } = useLanguage();
  const careersPath = getLocalizedPath('/careers', language);

  const {
    title = intl.formatMessage({ id: 'careers.title' }),
    description = intl.formatMessage({ id: 'careers.description' }),
    employeeCount = 85,
    partnershipNote = intl.formatMessage({ id: 'careers.partnership.note' }),
    teamImage,
    locations: rawLocations = [],
    benefits: rawBenefits = []
  } = careersData || {};

  const benefits = rawBenefits.map((benefit) => benefit.name);

  const teamImageUrl = teamImage
    ? `${teamImage.url}`
    : 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80';

  const locations =
    rawLocations.length > 0
      ? rawLocations
      : [
          intl.formatMessage({ id: 'cameroon' }),
          intl.formatMessage({ id: 'kenya' }),
          intl.formatMessage({ id: 'nigeria' }),
          intl.formatMessage({ id: 'benin' }),
          intl.formatMessage({ id: 'south.africa' })
        ];

  return (
    <section className="bg-[#0a1628] text-white overflow-hidden">
      <div className="relative w-full h-[36vh] min-h-[240px] max-h-[400px] overflow-hidden">
        <SkeletonImage
          src={teamImageUrl}
          alt="Équipe d'Africa Systems"
          className="w-full h-[36vh] min-h-[240px] max-h-[400px]"
          imgClassName="w-full h-full object-cover opacity-70"
          onDark
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/40 to-[#0a1628]/20" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 -mt-20 relative z-10 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-20">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-[#16A34A]" />
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                Carrières
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
              {title}
            </h2>
            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-xl">
              {description}
            </p>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 grid grid-cols-3 gap-6 lg:gap-4 items-end">
            <div>
              <div className="text-4xl md:text-5xl font-bold tabular-nums tracking-tight">
                {employeeCount}+
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#16A34A] font-semibold leading-snug">
                {intl.formatMessage({ id: 'employees.across.africa' })}
              </div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold tabular-nums tracking-tight">
                {String(locations.length).padStart(2, '0')}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#16A34A] font-semibold leading-snug">
                {intl.formatMessage({ id: 'countries.with.opportunities' })}
              </div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold tabular-nums tracking-tight">05</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#16A34A] font-semibold leading-snug">
                {intl.formatMessage({ id: 'continents.of.impact' })}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          <div className="lg:col-span-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-6">
              {intl.formatMessage({ id: 'our.benefits' })}
            </h3>
            <ul className="space-y-0">
              {benefits.map((benefit, index) => (
                <li
                  key={index}
                  className="flex items-start gap-4 py-3.5 border-b border-white/10 text-white/85"
                >
                  <span className="text-[#16A34A] mt-0.5">—</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-between gap-8">
            <div className="border-l-2 border-[#16A34A] pl-6">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
                {intl.formatMessage({ id: 'academic.partnerships' })}
              </h3>
              <p className="text-slate-300 leading-relaxed text-base md:text-lg">{partnershipNote}</p>
            </div>

            <div className="pt-4">
              <p className="text-slate-400 mb-5 text-sm md:text-base">
                {intl.formatMessage({ id: 'spontaneous.application.text' })}
              </p>
              <Link
                to={careersPath}
                className="inline-flex items-center gap-3 bg-[#16A34A] hover:bg-[#15803D] text-white px-7 py-3.5 text-xs font-semibold tracking-[0.14em] uppercase transition-colors"
              >
                {intl.formatMessage({ id: 'spontaneous.application.button' })}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareersSection;
